// @ts-nocheck
"use client";

import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  MessageSquare, 
  Star, 
  CheckCircle, 
  Send,
  AlertCircle,
  User,
  Plus,
  X,
  ChevronUp,
  ChevronDown,
  Activity,
  ThermometerSun,
  NotebookPen,
  ClipboardList,
  Calendar,
  UserCheck,
  Award
} from "lucide-react";
import { toast } from "react-hot-toast";
import { api } from "@/lib/api";

interface ConductResponse {
  questionText: string;
  questionType: 'text' | 'rating' | 'multiple_choice' | 'yes_no' | 'likert';
  questionOptions: string[];
  responseText?: string;
  responseRating?: number;
  responseChoice?: string;
  responseMultipleChoice?: string[];
  createdByRole: 'manager' | 'employee';
  questionOrder: number;
  contextNotes?: string;
  isDefault?: boolean; // Track whether this is a default question
}

interface ActionItem {
  id?: string;
  description: string;
  assignedTo?: string;
  assignedBy?: string;
  dueDate?: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in_progress' | 'completed';
  completionNotes?: string;
  sessionId?: string; // Reference to the session this action item was created in
  originSessionId?: string | null; // Original session the action item was created in
}

interface Achievement {
  id?: string;
  achievement_text: string;
  created_by?: string;
  created_by_role?: 'manager' | 'employee';
  session_id?: string;
}

interface SessionData {
  id: string;
  title: string;
  workflowType: string;
  manager: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
  };
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
  };
  team?: {
    id: string;
    name: string;
  } | null;
  questions?: any[]; // Setup questions from the recurring setup
  existingResponses?: any[]; // Existing responses from database
  existingRatings?: {
    mood_rating?: number;
    productivity_rating?: number;
    temperature_rating?: number;
  } | null;
  existingActionItems?: ActionItem[]; // Existing action items from database
  existingAchievements?: Achievement[]; // Existing achievements from database
  sessionExists?: boolean; // Flag to indicate if session already exists
  isCompleted?: boolean; // Flag to indicate if session is completed
  completedAt?: string | null; // Completion timestamp
  notes?: string | null;
  token: string;
  isPublic: boolean;
}

interface PublicConductFormProps {
  sessionData: SessionData;
}

interface FormData {
  responses: ConductResponse[];
  additionalNotes?: string;
  actionItems?: ActionItem[];
  achievements?: Achievement[];
  moodRating?: number;
  productivityRating?: number;
  temperatureRating?: number;
}

export function PublicConductForm({ sessionData }: PublicConductFormProps) {
  const [responses, setResponses] = useState<ConductResponse[]>([]);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [moodRating, setMoodRating] = useState<number | undefined>(undefined);
  const [productivityRating, setProductivityRating] = useState<number | undefined>(undefined);
  const [temperatureRating, setTemperatureRating] = useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [completionData, setCompletionData] = useState<any>(null);
  
  // Check if session is completed
  if (sessionData.isCompleted) {
    const setupQuestions = sessionData.questions || [];
    const existingResponses = sessionData.existingResponses || {};
    const existingRatings = sessionData.existingRatings;
    const existingActionItems = sessionData.existingActionItems || [];
    const existingAchievements = sessionData.existingAchievements || [];
    const additionalNotes = sessionData.notes?.trim();

    // Helper function to get user name from ID
    const getUserName = (userId: string): string => {
      if (userId === sessionData.manager.id) {
        return sessionData.manager.fullName;
      }
      if (userId === sessionData.employee.id) {
        return sessionData.employee.fullName;
      }
      return userId; // fallback to showing ID if not found
    };

    return (
      <div className="w-full max-w-4xl mx-auto space-y-6">
        {/* Completion Header */}
        <Card className="border-2 border-success/20 bg-success/5">
          <CardContent className="pt-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>
              <h2 className="text-2xl font-bold text-success mb-2">Session Already Completed</h2>
              <p className="text-success/80 mb-4">
                This 1-on-1 session has already been completed and submitted. The responses cannot be modified.
              </p>
              <div className="bg-card p-4 rounded-lg border border-border mb-4">
                <div className="text-left space-y-2">
                  <div className="text-sm">
                    <strong>Session:</strong> {sessionData.title || '1-on-1 Session'}
                  </div>
                  <div className="text-sm">
                    <strong>Participants:</strong> {sessionData.manager.fullName} & {sessionData.employee.fullName}
                  </div>
                  {sessionData.team && (
                    <div className="text-sm">
                      <strong>Team:</strong> {sessionData.team.name}
                    </div>
                  )}
                  {sessionData.completedAt && (
                    <div className="text-sm">
                      <strong>Completed:</strong> {new Date(sessionData.completedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm text-success/80">
                If you need to create a new 1-on-1 session, please go back to your dashboard and create a new session from the setup.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Completed Questions and Responses */}
        {setupQuestions.length > 0 && (
          <Card className="border border-border bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Questions & Responses
              </CardTitle>
              <CardDescription>
                Review the questions and responses that were submitted for this session.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {setupQuestions.map((question: any, index: number) => {
                // Response is keyed by question text, not index
                const response = existingResponses[question.text];
                return (
                  <div key={index} className="bg-card p-4 rounded-lg border border-border">
                    <div className="mb-3">
                      <h4 className="font-medium text-foreground mb-1">
                        Question {index + 1}
                      </h4>
                      <p className="text-foreground-secondary">{question.text}</p>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {question.type}
                      </Badge>
                    </div>
                    <div className="border-t pt-3">
                      <p className="text-sm font-medium text-foreground-secondary mb-1">Response:</p>
                      {response ? (
                        <div className="bg-muted p-3 rounded border">
                          {question.type === 'rating' && (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center">
                                {[...Array(10)].map((_, i) => (
                                  <Star 
                                    key={i} 
                                    className={`w-4 h-4 ${
                                      i < (response.value || 0) 
                                        ? 'text-warning fill-current' 
                                        : 'text-muted-foreground'
                                    }`} 
                                  />
                                ))}
                              </div>
                              <span className="text-sm font-medium">
                                {response.value}/10
                              </span>
                            </div>
                          )}
                          {question.type === 'text' && (
                            <p className="text-foreground-secondary whitespace-pre-wrap">
                              {response.value || 'No response provided'}
                            </p>
                          )}
                          {(question.type === 'multiple_choice' || 
                            question.type === 'yes_no' || 
                            question.type === 'likert') && (
                            <p className="text-foreground-secondary">
                              {response.value || 'No choice selected'}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-muted-foreground italic">No response provided</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Additional Notes */}
        <Card className="border border-border bg-card">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <NotebookPen className="w-5 h-5 text-primary" />
              Additional Notes
            </CardTitle>
            <CardDescription>
              Summary notes captured when the session was submitted.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {additionalNotes ? (
              <p className="text-foreground-secondary whitespace-pre-wrap">{additionalNotes}</p>
            ) : (
              <p className="text-muted-foreground italic">No additional notes were recorded for this session.</p>
            )}
          </CardContent>
        </Card>

        {/* Completed Ratings */}
        {existingRatings && (existingRatings.mood_rating || existingRatings.productivity_rating || existingRatings.temperature_rating) && (
          <Card className="border border-border bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Session Ratings
              </CardTitle>
              <CardDescription>
                The ratings that were provided for this 1-on-1 session.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {existingRatings.mood_rating && (
                <div className="bg-card p-4 rounded-lg border border-border text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Star className="w-6 h-6 text-warning" />
                  </div>
                  <h4 className="font-medium text-foreground mb-1">Mood</h4>
                  <div className="flex items-center justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${
                          i < existingRatings.mood_rating 
                            ? 'text-warning fill-current' 
                            : 'text-muted-foreground'
                        }`} 
                      />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-secondary mt-1">
                    {existingRatings.mood_rating}/5
                  </p>
                </div>
              )}
              {existingRatings.productivity_rating && (
                <div className="bg-card p-4 rounded-lg border border-border text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Activity className="w-6 h-6 text-success" />
                  </div>
                  <h4 className="font-medium text-foreground mb-1">Productivity</h4>
                  <div className="flex items-center justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${
                          i < existingRatings.productivity_rating 
                            ? 'text-success fill-current' 
                            : 'text-muted-foreground'
                        }`} 
                      />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-secondary mt-1">
                    {existingRatings.productivity_rating}/5
                  </p>
                </div>
              )}
              {existingRatings.temperature_rating !== null && existingRatings.temperature_rating !== undefined && (
                <div className="bg-card p-4 rounded-lg border border-border text-center">
                  <div className="flex items-center justify-center mb-2">
                    <ThermometerSun className="w-6 h-6 text-destructive" />
                  </div>
                  <h4 className="font-medium text-foreground mb-1">Team Temperature</h4>
                  <div className="flex items-center justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${
                          i < existingRatings.temperature_rating 
                            ? 'text-destructive fill-current' 
                            : 'text-muted-foreground'
                        }`} 
                      />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-secondary mt-1">
                    {existingRatings.temperature_rating}/5
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Completed Action Items */}
        {existingActionItems && existingActionItems.length > 0 && (
          <Card className="border border-border bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary" />
                Action Items
              </CardTitle>
              <CardDescription>
                All action items for this manager-employee relationship.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                // Group action items by whether they were created during this session
                const currentSessionId = sessionData.id;
                const sessionItems = existingActionItems.filter((item: any) => item.sessionId === currentSessionId);
                const otherItems = existingActionItems.filter((item: any) => item.sessionId !== currentSessionId);
                
                return (
                  <>
                    {/* Items Created During This Session */}
                    {sessionItems.length > 0 && (
                      <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Plus className="w-4 h-4 text-success" />
                          <h4 className="font-semibold text-foreground">
                            Created During This Session ({sessionItems.length})
                          </h4>
                        </div>
                        <div className="space-y-2">
                          {sessionItems.map((item: any, index: number) => (
                            <div key={index} className="bg-success/10 border border-success/20 rounded-lg p-3">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-medium text-foreground flex-1">
                                  {item.description}
                                </h4>
                                <Badge 
                                  variant={item.status === 'completed' ? 'default' : item.status === 'in_progress' ? 'secondary' : 'outline'}
                                  className="ml-2"
                                >
                                  {item.status.replace('_', ' ')}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-foreground-secondary">
                                {item.assignedTo && (
                                  <div className="flex items-center gap-1">
                                    <UserCheck className="w-4 h-4" />
                                    <span>Assigned to: {getUserName(item.assignedTo)}</span>
                                  </div>
                                )}
                                {item.assignedBy && (
                                  <div className="flex items-center gap-1">
                                    <User className="w-4 h-4" />
                                    <span>Assigned by: {getUserName(item.assignedBy)}</span>
                                  </div>
                                )}
                                {item.dueDate && (
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    <span>Due: {new Date(item.dueDate).toLocaleDateString()}</span>
                                  </div>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  {item.priority}
                                </Badge>
                              </div>
                              {item.completionNotes && (
                                <div className="mt-2 p-2 bg-muted rounded border">
                                  <p className="text-sm text-foreground-secondary">
                                    <span className="font-medium">Notes:</span> {item.completionNotes}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Other Action Items */}
                    {otherItems.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <ClipboardList className="w-4 h-4 text-foreground-secondary" />
                          <h4 className="font-semibold text-foreground">
                            Other Action Items ({otherItems.length})
                          </h4>
                        </div>
                        <div className="space-y-2">
                          {otherItems.map((item: any, index: number) => (
                            <div key={index} className="bg-card p-4 rounded-lg border border-border">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-medium text-foreground flex-1">
                                  {item.description}
                                </h4>
                                <Badge 
                                  variant={item.status === 'completed' ? 'default' : item.status === 'in_progress' ? 'secondary' : 'outline'}
                                  className="ml-2"
                                >
                                  {item.status.replace('_', ' ')}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-foreground-secondary">
                                {item.assignedTo && (
                                  <div className="flex items-center gap-1">
                                    <UserCheck className="w-4 h-4" />
                                    <span>Assigned to: {getUserName(item.assignedTo)}</span>
                                  </div>
                                )}
                                {item.assignedBy && (
                                  <div className="flex items-center gap-1">
                                    <User className="w-4 h-4" />
                                    <span>Assigned by: {getUserName(item.assignedBy)}</span>
                                  </div>
                                )}
                                {item.dueDate && (
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    <span>Due: {new Date(item.dueDate).toLocaleDateString()}</span>
                                  </div>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  {item.priority}
                                </Badge>
                              </div>
                              {item.completionNotes && (
                                <div className="mt-2 p-2 bg-muted rounded border">
                                  <p className="text-sm text-foreground-secondary">
                                    <span className="font-medium">Notes:</span> {item.completionNotes}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Completed Achievements */}
        {existingAchievements && existingAchievements.length > 0 && (
          <Card className="border border-border bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Achievements
              </CardTitle>
              <CardDescription>
                Achievements and accomplishments recorded during this session.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {existingAchievements.map((achievement: any, index: number) => (
                <div key={index} className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Award className="w-4 h-4 text-success" />
                    </div>
                    <div className="flex-1">
                      <p className="text-foreground mb-2 leading-relaxed">
                        {achievement.achievement_text}
                      </p>
                      <div className="text-sm text-foreground-secondary">
                        <span className="font-medium">Recorded by:</span> {getUserName(achievement.created_by)}
                        {achievement.created_at && (
                          <span className="ml-4">
                            <span className="font-medium">Date:</span> {new Date(achievement.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Initialize with questions from the setup and load existing responses
  useEffect(() => {
    try {

      const setupQuestions = sessionData.questions || [];
      const existingResponses = sessionData.existingResponses || [];
      const existingRatings = sessionData.existingRatings;
      
      // If we have existing responses, use them
      if (existingResponses.length > 0) {
        const setupQuestionTexts = new Set(setupQuestions.map((q: any) => q.questionText || q.text));
        
        const loadedResponses: ConductResponse[] = existingResponses.map((existing: any, index: number) => {
          const questionText = existing.question_text || '';
          const isSetupQuestion = setupQuestionTexts.has(questionText);
          
          // Safe JSON parsing with fallback
          const parseJsonSafely = (jsonString: any, fallback: any = []) => {
            if (!jsonString || jsonString === null || jsonString === undefined) return fallback;
            try {
              if (typeof jsonString === 'string') {
                return JSON.parse(jsonString);
              }
              return jsonString; // Already parsed
            } catch (error) {
              console.warn('Failed to parse JSON:', {
                original: jsonString,
                type: typeof jsonString,
                error: error.message
              });
              return fallback;
            }
          };
          
          const response = {
            questionText,
            questionType: existing.question_type || 'text',
            questionOptions: parseJsonSafely(existing.question_options, []),
            responseText: existing.response_text || '',
            responseRating: existing.response_rating || undefined,
            responseChoice: existing.response_choice || '',
            responseMultipleChoice: parseJsonSafely(existing.response_multiple_choice, []),
            createdByRole: existing.created_by_role || 'manager',
            questionOrder: existing.question_order || index,
            contextNotes: existing.context_notes || '',
            isDefault: isSetupQuestion, // Only setup questions are read-only
          };
          
          
          return response;
        });
        
        setResponses(loadedResponses);
      } else {
        // Initialize with setup questions if no existing responses
        const initialResponses: ConductResponse[] = setupQuestions.map((question: any, index: number) => ({
          questionText: question.questionText || question.text || '',
          questionType: question.questionType || question.type || 'text',
          questionOptions: question.options || [],
          responseText: '',
          responseRating: undefined,
          responseChoice: '',
          responseMultipleChoice: [],
          createdByRole: 'manager', // Default role
          questionOrder: index,
          contextNotes: '',
          isDefault: true, // Mark setup questions as read-only
        }));
        
        setResponses(initialResponses);
      }
      
      // Load existing ratings
      if (existingRatings) {
        setMoodRating(existingRatings.mood_rating || undefined);
        setProductivityRating(existingRatings.productivity_rating || undefined);
        setTemperatureRating(existingRatings.temperature_rating || undefined);
      }

      // Load existing action items
      if (sessionData.existingActionItems) {
        const normalizedActionItems = sessionData.existingActionItems.map((item) => {
          const description = (item.description ?? '').trim();

          return {
            ...item,
            description,
            assignedTo: item.assignedTo || undefined,
            assignedBy: item.assignedBy || undefined,
            sessionId: sessionData.id,
            originSessionId: item.originSessionId ?? item.sessionId ?? null,
          };
        });

        setActionItems(normalizedActionItems);
      }

      // Load existing achievements
      if (sessionData.existingAchievements) {
        setAchievements(sessionData.existingAchievements);
      }

      // Load existing additional notes
      if (typeof sessionData.notes === 'string') {
        setAdditionalNotes(sessionData.notes);
      } else {
        setAdditionalNotes('');
      }
    } catch (error) {
      console.error('Error initializing PublicConductForm:', error);
      // Fallback to empty responses if there's an error
      setResponses([]);
    }
  }, [sessionData.questions, sessionData.existingResponses, sessionData.existingRatings, sessionData.existingActionItems, sessionData.existingAchievements, sessionData.notes]);

  const updateResponse = (index: number, updatedResponse: ConductResponse) => {
    const newResponses = [...responses];
    newResponses[index] = updatedResponse;
    setResponses(newResponses);
  };

  const addQuestion = () => {
    const newResponse: ConductResponse = {
      questionText: '',
      questionType: 'text',
      questionOptions: [],
      responseText: '',
      responseRating: undefined,
      responseChoice: '',
      responseMultipleChoice: [],
      createdByRole: 'manager',
      questionOrder: responses.length,
      contextNotes: '',
      isDefault: false, // New questions are not default
    };
    
    setResponses([...responses, newResponse]);
  };

  const removeResponse = (index: number) => {
    const newResponses = [...responses];
    newResponses.splice(index, 1);
    setResponses(newResponses);
  };

  const getCompletedCount = (): number => {
    return responses.filter(response => {
      if (!response.questionText.trim()) return false;
      
      switch (response.questionType) {
        case 'text':
          return !!response.responseText?.trim();
        case 'rating':
          return typeof response.responseRating === 'number';
        case 'multiple_choice':
          return !!response.responseChoice;
        case 'yes_no':
          return !!response.responseChoice;
        case 'likert':
          return !!response.responseChoice;
        default:
          return true;
      }
    }).length;
  };

  const isFormValid = (): boolean => {
    // Check all questions are answered
    const questionsValid = responses.every(response => {
      // For default questions, question text is always valid (pre-filled)
      // For new questions, require question text to be filled
      if (!response.isDefault && !response.questionText.trim()) return false;
      
      switch (response.questionType) {
        case 'text':
          return !!response.responseText?.trim();
        case 'rating':
          return typeof response.responseRating === 'number';
        case 'multiple_choice':
          return !!response.responseChoice;
        case 'yes_no':
          return !!response.responseChoice;
        case 'likert':
          return !!response.responseChoice;
        default:
          return true;
      }
    });
    
    // Check all three ratings are provided
    const ratingsValid = moodRating !== undefined && productivityRating !== undefined && temperatureRating !== undefined;
    
    return questionsValid && ratingsValid;
  };

  const getValidationMessage = (): string => {
    const questionsValid = responses.every(response => {
      if (!response.isDefault && !response.questionText.trim()) return false;
      
      switch (response.questionType) {
        case 'text':
          return !!response.responseText?.trim();
        case 'rating':
          return typeof response.responseRating === 'number';
        case 'multiple_choice':
        case 'yes_no':
        case 'likert':
          return !!response.responseChoice;
        default:
          return true;
      }
    });
    
    const ratingsValid = moodRating !== undefined && productivityRating !== undefined && temperatureRating !== undefined;
    
    if (!questionsValid && !ratingsValid) {
      return 'Please complete all questions and provide all three ratings';
    } else if (!questionsValid) {
      return 'Please complete all questions';
    } else if (!ratingsValid) {
      return 'Please provide all three ratings: Mood, Productivity, and Team Temperature';
    }
    
    return '';
  };

  const handleSubmit = async () => {
    const validationMessage = getValidationMessage();
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData: FormData = {
        responses: responses.map((response, index) => ({
          ...response,
          questionOrder: index
        })),
        additionalNotes,
        actionItems,
        achievements,
        moodRating,
        productivityRating,
        temperatureRating
      };

      const result = await api.post(`/conduct/${sessionData.token}`, formData);

      if (!result.success) {
        // Handle detailed validation errors from the API
        if (result.details && Array.isArray(result.details)) {
          // Show the main message and the first few specific errors
          const mainMessage = result.message || 'Failed to submit 1-on-1 session';
          const detailMessage = result.details.slice(0, 3).join(', ');
          toast.error(`${mainMessage}${detailMessage ? ': ' + detailMessage : ''}`);
        } else {
          toast.error(result.message || result.error || 'Failed to submit 1-on-1 session');
        }
        return;
      }
      
      // Store the completion data and show success screen
      setCompletionData(result.data);
      setShowSuccess(true);
      toast.success('1-on-1 session completed successfully');
      
    } catch (error: any) {
      console.error('Error submitting 1-on-1 session:', error);
      toast.error(error.message || 'Failed to submit session');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    // Helper function to get user name from ID
    const getUserName = (userId: string): string => {
      if (userId === sessionData.manager.id) {
        return sessionData.manager.fullName;
      }
      if (userId === sessionData.employee.id) {
        return sessionData.employee.fullName;
      }
      return userId; // fallback to showing ID if not found
    };

    return (
      <div className="w-full max-w-4xl mx-auto space-y-6">
        {/* Main Success Message */}
        <Card className="w-full">
          <CardContent className="pt-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Session Complete!</h2>
              <p className="text-muted-foreground mb-6">
                Your 1-on-1 session has been completed and recorded successfully. Thank you for participating!
              </p>
              
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-success">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Session Submitted</span>
                </div>
                <p className="text-sm text-success/80 mt-1">
                  Your responses and ratings have been recorded for this 1-on-1 meeting.
                </p>
              </div>

              <div className="text-sm text-muted-foreground">
                <p className="mt-1">
                  Session: <strong>{sessionData.title}</strong>
                </p>
                <p className="mt-1">
                  Participants: <strong>{sessionData.manager.fullName}</strong> & <strong>{sessionData.employee.fullName}</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Items Summary */}
        {completionData?.actionItemsSummary && (completionData.actionItemsSummary.totalCreated > 0 || completionData.actionItemsSummary.totalUpdated > 0) && (
          <Card className="border-2 border-border bg-muted">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <ClipboardList className="w-5 h-5" />
                Action Items Summary
              </CardTitle>
              <CardDescription className="text-primary">
                Here's a summary of the action items from this 1-on-1 session.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Newly Created Action Items */}
              {completionData.actionItemsSummary.totalCreated > 0 && (
                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Plus className="w-4 h-4 text-success" />
                    <h4 className="font-semibold text-foreground">
                      New Action Items Created ({completionData.actionItemsSummary.totalCreated})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {completionData.actionItemsSummary.newItems.map((item: any, index: number) => (
                      <div key={index} className="bg-success/10 border border-success/20 rounded-lg p-3">
                        <div className="font-medium text-foreground mb-1">
                          {item.description}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-foreground-secondary">
                          {item.assigned_to && (
                            <div className="flex items-center gap-1">
                              <UserCheck className="w-4 h-4" />
                              <span>Assigned to: {getUserName(item.assigned_to)}</span>
                            </div>
                          )}
                          {item.due_date && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>Due: {new Date(item.due_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {item.priority}
                          </Badge>
                          <Badge 
                            variant={item.status === 'completed' ? 'default' : item.status === 'in_progress' ? 'secondary' : 'outline'}
                            className="text-xs"
                          >
                            {item.status === 'in_progress' ? 'in progress' : item.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Updated Existing Action Items */}
              {completionData.actionItemsSummary.totalUpdated > 0 && (
                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="w-4 h-4 text-primary" />
                    <h4 className="font-semibold text-foreground">
                      Existing Action Items Updated ({completionData.actionItemsSummary.totalUpdated})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {completionData.actionItemsSummary.updatedItems.map((item: any, index: number) => (
                      <div key={index} className="bg-muted border border-border rounded-lg p-3">
                        <div className="font-medium text-foreground mb-1">
                          {item.description}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-foreground-secondary">
                          {item.assignedTo && (
                            <div className="flex items-center gap-1">
                              <UserCheck className="w-4 h-4" />
                              <span>Assigned to: {getUserName(item.assignedTo)}</span>
                            </div>
                          )}
                          {item.dueDate && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>Due: {new Date(item.dueDate).toLocaleDateString()}</span>
                            </div>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {item.priority}
                          </Badge>
                          <Badge 
                            variant={item.status === 'completed' ? 'default' : item.status === 'in_progress' ? 'secondary' : 'outline'}
                            className="text-xs"
                          >
                            {item.status === 'in_progress' ? 'in progress' : item.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-surface-variant rounded-lg p-3 text-center">
                <p className="text-sm text-foreground">
                  <strong>Total Action Items:</strong> {completionData.actionItemsSummary.totalCreated} created, {completionData.actionItemsSummary.totalUpdated} updated
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Action Items Message */}
        {completionData?.actionItemsSummary && completionData.actionItemsSummary.totalCreated === 0 && completionData.actionItemsSummary.totalUpdated === 0 && (
          <Card className="border border-border">
            <CardContent className="pt-6 text-center">
              <div className="text-muted-foreground">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p>No action items were created or updated in this session.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Close Instructions */}
        <Card className="border border-border">
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-muted-foreground">
              You may now close this page. All information has been saved.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = responses.length > 0 ? ((getCompletedCount() / responses.length) * 100) : 0;

  return (
    <div className="space-y-8">
      {/* Session Info */}
      <Card className="border-2 border-primary/20 bg-card">
        <CardHeader>
          <CardTitle className="text-xl text-foreground flex items-center gap-2">
            <User className="h-5 w-5" />
            {sessionData.title}
          </CardTitle>
          <CardDescription className="text-foreground-secondary">
            Physical 1-on-1 Meeting between {sessionData.manager.fullName} and {sessionData.employee.fullName}
            {sessionData.team && ` • Team: ${sessionData.team.name}`}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-muted-foreground mb-2">
          <span>Progress</span>
          <span>{getCompletedCount()} of {responses.length} completed</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Questions */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h3 className="text-xl font-bold text-foreground">Meeting Questions</h3>
        </div>

        {responses.map((response, index) => (
          <QuestionResponseItem
            key={index}
            response={response}
            index={index}
            onUpdate={updateResponse}
            onRemove={removeResponse}
            canRemove={!response.isDefault}
          />
        ))}
        
        {/* Add Question Button */}
        <div className="text-center pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={addQuestion}
            className="gap-2 rounded-xl border-2 border-primary/20 text-primary hover:bg-primary/5"
          >
            <Plus className="h-4 w-4" /> Add Question
          </Button>
        </div>
      </div>

      {/* Ratings */}
      <Card className="border-2 border-primary/20 bg-card">
        <CardHeader>
          <CardTitle className="text-lg text-foreground flex items-center gap-2">
            <Star className="h-5 w-5" />
            Meeting Ratings
            <Badge variant="secondary" size="sm" className="ml-2">
              All 3 required
            </Badge>
          </CardTitle>
          <CardDescription className="text-foreground-secondary">
            Rate all three aspects of the meeting (all ratings are required)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Mood Rating */}
            <div className="space-y-3">
              <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Star className="h-4 w-4" />
                Mood Rating
                {moodRating !== undefined && (
                  <CheckCircle className="h-4 w-4 text-success" />
                )}
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setMoodRating(rating)}
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                      moodRating === rating
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-primary/10 text-muted-foreground hover:text-primary'
                    }`}
                  >
                    {rating}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">1 = Poor, 5 = Excellent</p>
            </div>

            {/* Productivity Rating */}
            <div className="space-y-3">
              <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Productivity
                {productivityRating !== undefined && (
                  <CheckCircle className="h-4 w-4 text-success" />
                )}
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setProductivityRating(rating)}
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                      productivityRating === rating
                        ? 'bg-success text-success-foreground'
                        : 'bg-muted hover:bg-success/10 text-muted-foreground hover:text-success'
                    }`}
                  >
                    {rating}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">1 = Low, 5 = High</p>
            </div>

            {/* Temperature Rating */}
            <div className="space-y-3">
              <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                <ThermometerSun className="h-4 w-4" />
                Team Temperature
                {temperatureRating !== undefined && (
                  <CheckCircle className="h-4 w-4 text-success" />
                )}
              </label>
              <div className="flex items-center gap-1">
                {[0, 1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setTemperatureRating(rating)}
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                      temperatureRating === rating
                        ? 'bg-warning text-warning-foreground'
                        : 'bg-muted hover:bg-warning/10 text-muted-foreground hover:text-warning'
                    }`}
                  >
                    {rating}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">0 = Cold, 5 = Hot</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Action Items
          </CardTitle>
          <CardDescription>
            Add specific action items and follow-ups from this meeting
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {actionItems.map((item, index) => (
            <ActionItemInput
              key={index}
              item={item}
              index={index}
              sessionData={sessionData}
              onUpdate={(updatedItem) => {
                const newItems = [...actionItems];
                newItems[index] = updatedItem;
                setActionItems(newItems);
              }}
              onRemove={() => {
                const newItems = actionItems.filter((_, i) => i !== index);
                setActionItems(newItems);
              }}
            />
          ))}
          
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => {
              setActionItems([...actionItems, {
                description: '',
                priority: 'medium',
                status: 'pending'
              }]);
            }}
            className="w-full gap-2 rounded-xl border-2 border-dashed border-primary/20 text-primary hover:bg-primary/5"
          >
            <Plus className="h-4 w-4" /> Add Action Item
          </Button>
        </CardContent>
      </Card>

      {/* Achievements */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Award className="h-5 w-5" />
            Achievements
          </CardTitle>
          <CardDescription>
            Record significant achievements, accomplishments, or positive developments discussed during this session
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {achievements.map((achievement, index) => (
            <AchievementInput
              key={index}
              achievement={achievement}
              index={index}
              onUpdate={(updatedAchievement) => {
                const newAchievements = [...achievements];
                newAchievements[index] = updatedAchievement;
                setAchievements(newAchievements);
              }}
              onRemove={() => {
                const newAchievements = achievements.filter((_, i) => i !== index);
                setAchievements(newAchievements);
              }}
            />
          ))}
          
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => {
              setAchievements([...achievements, {
                achievement_text: '',
                created_by_role: 'manager'
              }]);
            }}
            className="w-full gap-2 rounded-xl border-2 border-dashed border-success/20 text-success hover:bg-success/5"
          >
            <Plus className="h-4 w-4" /> Add Achievement
          </Button>
        </CardContent>
      </Card>

      {/* Additional Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <NotebookPen className="h-5 w-5" />
            Additional Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea 
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            placeholder="Add any additional notes or observations from the meeting..."
            rows={4}
            className="rounded-xl border-2 border-border focus:border-primary focus:ring-0"
          />
        </CardContent>
      </Card>

      {/* Submit Section */}
      <Card className="border-2 border-primary/20 bg-card">
        <CardContent className="pt-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-foreground mb-2">Ready to Submit?</h3>
            <p className="text-muted-foreground mb-4">
              Please review your responses and ratings before submitting. This will complete your 1-on-1 session.
            </p>
            
            <div className="flex flex-col items-center gap-4 mb-6">
              <div className="text-sm text-muted-foreground">
                Questions Completed: {getCompletedCount()} / {responses.length}
              </div>
              <div className="text-sm text-muted-foreground">
                Ratings: {[moodRating, productivityRating, temperatureRating].filter(r => r !== undefined).length} / 3 provided
              </div>
              {!isFormValid() && (
                <div className="text-center">
                  <Badge variant="destructive" size="sm" className="mb-2">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Incomplete submission
                  </Badge>
                  <p className="text-sm text-destructive">
                    {getValidationMessage()}
                  </p>
                </div>
              )}
            </div>
            
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              size="lg"
              className="min-w-[160px] bg-primary hover:bg-primary-hover"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 w-4 mr-2" />
                  Complete Session
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Question response component
function QuestionResponseItem({ 
  response, 
  index, 
  onUpdate, 
  onRemove, 
  canRemove
}: any) {
  const [showAdditionalContext, setShowAdditionalContext] = useState(false);
  
  const handleResponseChange = (field: string, value: any) => {
    onUpdate(index, { ...response, [field]: value });
  };

  const renderResponseInput = () => {
    switch (response.questionType) {
      case 'text':
        return (
          <Textarea
            value={response.responseText || ''}
            onChange={(e) => handleResponseChange('responseText', e.target.value)}
            placeholder="Enter your response..."
            className="mt-3 rounded-xl border-2 border-border focus:border-indigo-500 focus:ring-0"
            rows={3}
          />
        );

      case 'rating':
        return (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rating:</span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => handleResponseChange('responseRating', rating)}
                    className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                      response.responseRating === rating
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-primary/10 text-muted-foreground hover:text-primary'
                    }`}
                  >
                    {rating}
                  </button>
                ))}
              </div>
            </div>
            {response.responseRating && (
              <p className="text-xs text-primary">Selected: {response.responseRating}/10</p>
            )}
          </div>
        );

      case 'multiple_choice':
        return (
          <div className="mt-3 space-y-2">
            {response.questionOptions?.map((option: string, optionIndex: number) => (
              <label key={optionIndex} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-muted">
                <input
                  type="radio"
                  name={`question-${index}`}
                  value={option}
                  checked={response.responseChoice === option}
                  onChange={(e) => handleResponseChange('responseChoice', e.target.value)}
                  className="h-4 w-4 text-primary bg-background border border-border rounded focus:ring-primary"
                />
                <span className="text-sm text-foreground">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'yes_no':
        return (
          <div className="mt-3 flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`question-${index}`}
                value="yes"
                checked={response.responseChoice === 'yes'}
                onChange={(e) => handleResponseChange('responseChoice', 'yes')}
                className="h-4 w-4 text-primary bg-background border border-border rounded focus:ring-primary"
              />
              <span className="text-sm text-foreground">Yes</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`question-${index}`}
                value="no"
                checked={response.responseChoice === 'no'}
                onChange={(e) => handleResponseChange('responseChoice', 'no')}
                className="h-4 w-4 text-primary bg-background border border-border rounded focus:ring-primary"
              />
              <span className="text-sm text-foreground">No</span>
            </label>
          </div>
        );

      case 'likert':
        return (
          <div className="mt-3 space-y-2">
            {response.questionOptions?.map((option: string, optionIndex: number) => (
              <label key={optionIndex} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-muted">
                <input
                  type="radio"
                  name={`question-${index}`}
                  value={option}
                  checked={response.responseChoice === option}
                  onChange={(e) => handleResponseChange('responseChoice', e.target.value)}
                  className="h-4 w-4 text-primary bg-background border border-border rounded focus:ring-primary"
                />
                <span className="text-sm text-foreground">{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="border-2 hover:border-border transition-colors">
      <CardContent className="pt-6">
        <div className="relative">
          <div className="flex-1">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {response.questionType === "text" ? "Text Response" :
                     response.questionType === "rating" ? "Rating (1-10)" :
                     response.questionType === "yes_no" ? "Yes/No" :
                     response.questionType === "likert" ? "Likert Scale" :
                     "Multiple Choice"}
                  </Badge>
                  {response.isDefault && (
                    <Badge variant="outline" className="text-xs bg-muted text-primary border-border">
                      Setup Question
                    </Badge>
                  )}
                </div>
                
                {/* Question text input */}
                <Input 
                  value={response.questionText} 
                  onChange={(e) => handleResponseChange('questionText', e.target.value)}
                  placeholder="Enter question text"
                  readOnly={response.isDefault}
                  className={`h-12 rounded-xl border-2 mb-3 ${
                    response.isDefault 
                      ? 'border-muted bg-muted/50 text-muted-foreground cursor-not-allowed'
                      : 'border-border focus:border-primary focus:ring-0'
                  }`}
                />
              </div>
              
              {/* Remove button */}
              {canRemove && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon"
                  className="h-10 w-10 rounded-xl border-2 border-destructive/20 text-destructive hover:bg-destructive/10 ml-4"
                  onClick={() => onRemove(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Response input */}
            {renderResponseInput()}
            
            {/* Context section with toggle */}
            <div className="mt-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAdditionalContext(!showAdditionalContext)}
                className="text-muted-foreground hover:text-foreground p-0 h-auto font-normal"
              >
                {showAdditionalContext ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Hide Additional Context
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Add Additional Context
                  </>
                )}
              </Button>
              
              {showAdditionalContext && (
                <div className="mt-2">
                  <Textarea
                    value={response.contextNotes || ''}
                    onChange={(e) => handleResponseChange('contextNotes', e.target.value)}
                    placeholder="Add any additional context, notes, or observations about this question..."
                    className="rounded-xl border-2 border-border focus:border-primary focus:ring-0"
                    rows={2}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Action Item component
function ActionItemInput({ 
  item, 
  index, 
  sessionData,
  onUpdate, 
  onRemove
}: {
  item: ActionItem;
  index: number;
  sessionData: SessionData;
  onUpdate: (item: ActionItem) => void;
  onRemove: () => void;
}) {
  const handleChange = (field: keyof ActionItem, value: any) => {
    onUpdate({ ...item, [field]: value });
  };

  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const handleDateChange = (dateString: string) => {
    if (!dateString) {
      handleChange('dueDate', undefined);
    } else {
      // Convert to ISO date string
      const date = new Date(dateString);
      handleChange('dueDate', date.toISOString().split('T')[0]);
    }
  };

  return (
    <Card className="border border-border">
      <CardContent className="pt-4">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </span>
                <Badge variant="secondary" className="text-xs">
                  Action Item
                </Badge>
                <Badge 
                  variant={item.priority === 'high' ? 'destructive' : item.priority === 'medium' ? 'default' : 'secondary'}
                  className="text-xs"
                >
                  {item.priority}
                </Badge>
                <Badge 
                  variant={item.status === 'completed' ? 'default' : item.status === 'in_progress' ? 'secondary' : 'outline'}
                  className="text-xs"
                >
                  {item.status === 'in_progress' ? 'in progress' : item.status}
                </Badge>
              </div>
              
              {/* Description */}
              <Textarea
                value={item.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe the action item..."
                className="rounded-xl border-2 border-border focus:border-primary focus:ring-0 mb-3"
                rows={2}
              />
              
              {/* Assignment, Due Date, and Status Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* Assigned To */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">
                    Assigned To
                  </label>
                  <select
                    value={item.assignedTo || ''}
                    onChange={(e) => handleChange('assignedTo', e.target.value || undefined)}
                    className="w-full rounded-lg border-2 border-border focus:border-primary focus:ring-0 p-2 bg-background"
                  >
                    <option value="">Not assigned</option>
                    <option value={sessionData.manager.id}>{sessionData.manager.fullName} (Manager)</option>
                    <option value={sessionData.employee.id}>{sessionData.employee.fullName} (Employee)</option>
                  </select>
                </div>
                
                {/* Due Date */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">
                    Due Date
                  </label>
                  <div className="relative">
                    <Input
                      type="date"
                      value={formatDateForInput(item.dueDate)}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="rounded-lg border-2 border-border focus:border-primary focus:ring-0"
                    />
                    <Calendar className="absolute right-2 top-2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  </div>
                </div>
                
                {/* Priority */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">
                    Priority
                  </label>
                  <select
                    value={item.priority}
                    onChange={(e) => handleChange('priority', e.target.value)}
                    className="w-full rounded-lg border-2 border-border focus:border-primary focus:ring-0 p-2 bg-background"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                
                {/* Status */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">
                    Status
                  </label>
                  <select
                    value={item.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className="w-full rounded-lg border-2 border-border focus:border-primary focus:ring-0 p-2 bg-background"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Remove button */}
            <Button 
              type="button" 
              variant="ghost" 
              size="icon"
              className="h-10 w-10 rounded-xl border-2 border-destructive/20 text-destructive hover:bg-destructive/10 ml-4"
              onClick={onRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Achievement input component
function AchievementInput({ 
  achievement, 
  index, 
  onUpdate, 
  onRemove
}: {
  achievement: Achievement;
  index: number;
  onUpdate: (achievement: Achievement) => void;
  onRemove: () => void;
}) {
  const handleChange = (field: keyof Achievement, value: any) => {
    onUpdate({ ...achievement, [field]: value });
  };

  return (
    <Card className="border border-border">
      <CardContent className="pt-4">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-success/10 text-success rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </span>
                <Badge variant="secondary" className="text-xs bg-success/10 text-success border-success/20">
                  Achievement
                </Badge>
              </div>
              
              {/* Achievement Text */}
              <Textarea
                value={achievement.achievement_text}
                onChange={(e) => handleChange('achievement_text', e.target.value)}
                placeholder="Describe the achievement, accomplishment, or positive development..."
                className="rounded-xl border-2 border-border focus:border-success focus:ring-0"
                rows={3}
              />
            </div>
            
            {/* Remove button */}
            <Button 
              type="button" 
              variant="ghost" 
              size="icon"
              className="h-10 w-10 rounded-xl border-2 border-destructive/20 text-destructive hover:bg-destructive/10 ml-4"
              onClick={onRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
